import { cx } from "@emotion/css";
import {
	ChevronDown,
	type LucideIcon,
	ThumbsDown,
	ThumbsUp,
} from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type {
	DeckFlashcardsSort,
	DeckFlashcardWithStats,
	DeckStatsType,
} from "src/api/flashcardDecks";
import { TextBlackButton } from "src/components/Default/LekUI/Button";
import { Menu } from "src/components/Default/LekUI/Menu";
import { DeckFlashcardsList } from "src/components/NewComponents/Flashcards/DeckFlashcardsList";
import { FlashcardCard } from "src/components/NewComponents/Flashcards/FlashcardCard";
import { useGetDeckFlashcardsStats } from "src/hooks/useFlashcards";
import { useFontSize } from "src/hooks/useFontSize";

interface DeckFlashcardsStatsListProps {
	deckId: number;
	deckType: DeckStatsType;
	startDate?: string;
	endDate?: string;
}

interface SortOption {
	value: DeckFlashcardsSort;
	labelKey: string;
	labelFallback: string;
}

const SORT_OPTIONS: SortOption[] = [
	{
		value: "updatedAt",
		labelKey: "modals.deckStatistics.flashcardsSort.updatedAt",
		labelFallback: "Data aktualizacji",
	},
	{
		value: "bestKnown",
		labelKey: "modals.deckStatistics.flashcardsSort.bestKnown",
		labelFallback: "Najlepiej znane",
	},
	{
		value: "worstKnown",
		labelKey: "modals.deckStatistics.flashcardsSort.worstKnown",
		labelFallback: "Najgorzej znane",
	},
	{
		value: "mostAnswered",
		labelKey: "modals.deckStatistics.flashcardsSort.mostAnswered",
		labelFallback: "Najczęściej odpowiadane",
	},
	{
		value: "leastAnswered",
		labelKey: "modals.deckStatistics.flashcardsSort.leastAnswered",
		labelFallback: "Najrzadziej odpowiadane",
	},
];

interface GradeStat {
	grade: number;
	labelKey: string;
	labelFallback: string;
	tintClass: string;
	Icon?: LucideIcon;
}

const KNOW_DONT_KNOW_GRADES: GradeStat[] = [
	{
		grade: 1,
		labelKey: "decks.knowDontKnow.know",
		labelFallback: "Znam",
		tintClass: "tint-success",
		Icon: ThumbsUp,
	},
	{
		grade: 0,
		labelKey: "decks.knowDontKnow.dontKnow",
		labelFallback: "Nie znam",
		tintClass: "tint-error",
		Icon: ThumbsDown,
	},
];

const SPACED_REPETITION_GRADES: GradeStat[] = [
	{
		grade: 3,
		labelKey: "decks.gradeButtons.3",
		labelFallback: "Proste",
		tintClass: "tint-info",
	},
	{
		grade: 2,
		labelKey: "decks.gradeButtons.2",
		labelFallback: "Dobrze",
		tintClass: "tint-success",
	},
	{
		grade: 1,
		labelKey: "decks.gradeButtons.1",
		labelFallback: "Słabo",
		tintClass: "tint-warning",
	},
	{
		grade: 0,
		labelKey: "decks.gradeButtons.0",
		labelFallback: "Powtórz",
		tintClass: "tint-error",
	},
];

function FlashcardGradeStats({
	flashcard,
	deckType,
}: {
	flashcard: DeckFlashcardWithStats;
	deckType: DeckStatsType;
}) {
	const { t } = useTranslation();
	const grades =
		deckType === "know_dont_know"
			? KNOW_DONT_KNOW_GRADES
			: SPACED_REPETITION_GRADES;

	return grades.map(({ grade, labelKey, labelFallback, tintClass, Icon }) => (
		<span key={grade} className={cx("chip chip--status", tintClass)}>
			{Icon && <Icon className="icon-super-small" />}
			{t(labelKey, labelFallback)} {flashcard.stats?.[String(grade)] ?? 0}
		</span>
	));
}

export function DeckFlashcardsStatsList({
	deckId,
	deckType,
	startDate,
	endDate,
}: DeckFlashcardsStatsListProps) {
	const { t } = useTranslation();
	const { modalTextFontSizeClass } = useFontSize();
	const [sortIndex, setSortIndex] = useState(0);
	const currentSort = SORT_OPTIONS[sortIndex];
	const { data, isLoading } = useGetDeckFlashcardsStats(deckId, {
		startDate,
		endDate,
		sort: currentSort.value,
		type: deckType,
	});
	const flashcards = data?.flashcards ?? [];

	const sortMenuItems = SORT_OPTIONS.map((option, index) => ({
		title: t(option.labelKey, option.labelFallback),
		onClick: () => setSortIndex(index),
		selected: sortIndex === index,
		testId: `deck-flashcards-sort-${option.value}`,
	}));

	const toolbar = (
		<div className="flex justify-between items-center gap-12">
			<span className={cx(modalTextFontSizeClass, "text-grey")}>
				{t("modals.deckStatistics.cardsCount", "{{count}} fiszek", {
					count: flashcards.length,
				})}
			</span>
			<Menu
				items={sortMenuItems}
				closeAfterClick
				tooltip={t("decks.sort.tooltip", "Sortuj")}
				menuPopperClassName="z-index-9999"
				renderTrigger={({ onClick }) => (
					<TextBlackButton
						onClick={onClick}
						className="flex items-center gap-4"
						data-test-id="deck-flashcards-sort-button"
					>
						<span className="text-grey">
							{t("modals.deckStatistics.sortLabel", "Sortowanie:")}
						</span>
						<span className="font-semibold">
							{t(currentSort.labelKey, currentSort.labelFallback)}
						</span>
						<ChevronDown className="icon-small" />
					</TextBlackButton>
				)}
			/>
		</div>
	);

	return (
		<DeckFlashcardsList
			flashcards={flashcards}
			isLoading={isLoading}
			searchTestId="deck-stats-search-input"
			toolbar={toolbar}
			renderItem={(flashcard, query) => (
				<FlashcardCard
					key={flashcard.id}
					front={flashcard.front}
					back={flashcard.back}
					query={query}
					footer={
						<FlashcardGradeStats flashcard={flashcard} deckType={deckType} />
					}
				/>
			)}
		/>
	);
}
